<!--
📌 Componente: VitrineFooter.vue
📂 Caminho: src/components/VitrineFooter.vue
📝 Descrição: Rodapé da Vitrine Digital com contatos, horários, links
  sociais e consulta de produtos por ID para acompanhar reparos.

👁️ Interface e Interação com o Usuário:
  - Exibe endereços e contatos das lojas
  - Mostra horários gerais e de doação
  - Links para redes sociais (Instagram, Facebook)
  - Campo para consulta de produtos por ID com validação automática
  - Modal de detalhe de reparos e modal de não encontrado
  - Botões para abrir Impressum e Datenschutz
  - Link provisório para a homologação do novo site institucional
    (praxisgmbh-de.web.app), a ser removido quando praxisgmbh.de apontar
    para o novo frontend

📥 Entrada (Props):
  - nenhuma

📤 Saída (Emits):
  - `@open-legal` - emite string 'impressum' ou 'datenschutz'
    quando o usuário clica nos botões legais

🔗 Dependências:
  - **Componentes Vue:** VitrineReparatur, VitrineReparaturModal
  - **Utilitários JS:** nenhum
  - **Backend (Cloud Functions/API):** Firestore (consulta pontual)
  - **Bibliotecas externas:** TailwindCSS, Firebase SDK modular
-->

<template>
  <footer class="bg-texto-principal text-branco py-10">
    <div class="container mx-auto px-4 grid grid-cols-1 md:grid-cols-3 gap-8">
      <div>
        <p>
          <strong>Marburger Gebrauchtwarenkaufhaus</strong>
        </p>
        <p>
          {{ $t("vitrine.footer.tel") }}:
          <a
            href="tel:064218733319"
            class="inline-flex items-center min-h-11 align-middle underline hover:no-underline"
            @click.prevent="
              logContact('phone', 'kaufhaus');
              $event.target.closest('a').click();
            "
          >
            06421 87333-19
          </a>
        </p>
        <p>{{ $t("vitrine.footer.email") }}: kaufhaus@praxisgmbh.de</p>
        <p class="mt-4">
          <strong>relectro Elektro-Secondhand-Kaufhaus</strong>
        </p>
        <p>
          {{ $t("vitrine.footer.tel") }}:
          <a
            href="tel:06421870483"
            class="inline-flex items-center min-h-11 align-middle underline hover:no-underline"
            @click.prevent="
              logContact('phone', 'relectro');
              $event.target.closest('a').click();
            "
          >
            06421 870483
          </a>
        </p>
        <p>{{ $t("vitrine.footer.email") }}: relectro@praxisgmbh.de</p>
        <p class="mt-4">Gisselberger Straße 33</p>
        <p>35037 Marburg</p>
      </div>

      <div>
        <p>
          <strong>{{ $t("vitrine.hours.title") }}:</strong>
        </p>
        <p>{{ $t("vitrine.hours.general") }}</p>
        <p class="mt-4">
          <strong>{{ $t("vitrine.hours.donationTitle") }}:</strong>
        </p>
        <p>{{ $t("vitrine.hours.donationLine1") }}</p>
        <p>{{ $t("vitrine.hours.donationLine2") }}</p>
        <div class="flex space-x-4 mt-4">
          <a
            href="https://www.instagram.com/praxisgmbh/"
            target="_blank"
            class="inline-flex items-center justify-center min-h-11 min-w-11 hover:opacity-75"
            aria-label="Instagram"
            @click="logContact('social', 'instagram')"
          >
            <img
              src="@/assets/images/logo_instagram.png"
              alt="Instagram"
              class="w-6 h-6"
            />
          </a>
          <a
            href="https://www.facebook.com/praxisgmbh"
            target="_blank"
            class="inline-flex items-center justify-center min-h-11 min-w-11 hover:opacity-75"
            aria-label="Facebook"
            @click="logContact('social', 'facebook')"
          >
            <img
              src="@/assets/images/logo_facebook.png"
              alt="Facebook"
              class="w-6 h-6"
            />
          </a>
        </div>
        <!-- Botão instalar PWA (Vitrine) -->
        <div v-if="pwaInstall.canInstall" class="mt-3">
          <button
            type="button"
            @click="pwaInstall.install()"
            class="inline-flex items-center min-h-11 px-3 py-2 bg-branco text-cinza-900 rounded font-semibold hover:bg-cinza-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-azul-profundo focus:ring-offset-texto-principal transition-colors shadow-sm"
            :aria-label="$t('pwa.installBtn')"
          >
            <!-- Heroicons v1 Outline: download -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-azul-profundo"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            <span class="ml-2 text-sm">{{ $t("pwa.installBtn") }}</span>
          </button>
        </div>
      </div>

      <div>
        <h3 class="text-lg font-bold mb-4">
          {{ $t("vitrine.footer.productConsult") }}
        </h3>
        <input
          ref="productInput"
          v-model="productId"
          type="text"
          :placeholder="$t('vitrine.footer.placeholderId')"
          class="w-full max-w-xs p-2 rounded bg-cinza-700 text-branco border border-texto-secundario focus:outline-none focus:ring-2 focus:ring-azul-profundo disabled:opacity-60"
          :aria-invalid="productId && !isValidId ? 'true' : 'false'"
          :aria-busy="isSearching ? 'true' : 'false'"
          :disabled="isSearching"
          autocomplete="off"
          autocapitalize="characters"
          spellcheck="false"
          inputmode="text"
          @input="onInput"
          @keyup.enter.prevent="trySearch()"
          @blur="trySearch()"
        />
        <div class="mt-4 space-x-4">
          <button
            type="button"
            class="inline-flex items-center min-h-11 text-sm hover:underline"
            @click="$emit('open-legal', 'impressum')"
          >
            {{ $t("vitrine.footer.impressum") }}
          </button>
          <button
            type="button"
            class="inline-flex items-center min-h-11 text-sm hover:underline"
            @click="$emit('open-legal', 'datenschutz')"
          >
            {{ $t("vitrine.footer.datenschutz") }}
          </button>
        </div>

        <!-- Links de Navegação Secundária -->
        <div class="mt-4 max-w-xs space-y-3">
          <!-- Linha 1: Outdoor e Login (50/50) -->
          <div class="grid grid-cols-2 gap-3">
            <router-link
              to="/outdoor"
              aria-label="Outdoor-Ansicht"
              class="flex items-center justify-center min-h-11 px-3 py-2 bg-branco text-cinza-900 rounded font-semibold hover:bg-cinza-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-azul-profundo focus:ring-offset-texto-principal transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <span class="ml-2 text-sm">Outdoor</span>
            </router-link>

            <router-link
              v-if="appFlavor === 'praxis'"
              :to="{ name: 'login' }"
              target="_blank"
              rel="noopener noreferrer nofollow"
              class="flex items-center justify-center min-h-11 px-3 py-2 bg-branco text-cinza-900 rounded font-semibold hover:bg-cinza-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-azul-profundo focus:ring-offset-texto-principal transition-colors"
              aria-label="Login"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2m10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <span class="ml-2 text-sm">
                {{ $t("vitrine.login") }}
              </span>
            </router-link>
          </div>

          <!-- Linha 2: Spendenabholung (Full width) -->
          <router-link
            to="/spenden"
            :aria-label="$t('vitrine.menu.spendenabholung')"
            class="flex items-center justify-center w-full min-h-11 px-3 py-2 bg-branco text-cinza-900 rounded font-semibold hover:bg-cinza-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-azul-profundo focus:ring-offset-texto-principal transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
            <span class="ml-2 text-sm whitespace-nowrap">{{
              $t("vitrine.menu.spendenabholung")
            }}</span>
          </router-link>

          <!--
            Link provisório para a homologação do novo site institucional.
            Deve ser removido quando praxisgmbh.de apontar para o novo frontend.
          -->
          <a
            href="https://praxisgmbh-de.web.app"
            target="_blank"
            rel="noopener noreferrer nofollow"
            class="flex items-center justify-center w-full min-h-11 px-3 py-2 border border-dashed border-cinza-400 text-borda-suave rounded font-semibold hover:bg-cinza-700 hover:text-branco focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-azul-profundo focus:ring-offset-texto-principal transition-colors"
            aria-label="praxisgmbh.de – Vorschau der neuen Website, öffnet in einem neuen Fenster"
            @click="logContact('link', 'praxisgmbh-vorschau')"
          >
            <!-- Heroicons v1 Outline: external-link -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
            <span class="ml-2 text-sm whitespace-nowrap">
              praxisgmbh.de (Vorschau)
            </span>
          </a>
        </div>
      </div>
    </div>

    <!--
      Aviso de direitos no mesmo texto do site institucional, para que os dois
      rodapés fechem com a mesma assinatura.
    -->
    <div class="container mx-auto px-4 mt-4">
      <p class="block text-center text-sm py-2 text-cinza-400">
        {{ $t("vitrine.footer.copyright") }}
      </p>
    </div>

    <VitrineReparaturModal
      ref="notFoundModal"
      :product-id="notFoundId"
      :message="notFoundMessage"
      @close="onNotFoundClosed"
    />

    <VitrineReparatur
      v-model="showDetailModal"
      :produkt-id="detailProduktId"
      :produkt-name="detailProduktName"
      :marke="detailMarke"
      :reparaturen="reparaturen"
      :consulted-at="detailConsultedAt"
      @closed="onDetailClosed"
    />
  </footer>
</template>

<script>
import { nextTick } from "vue";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { analytics, logEvent } from "@/firebaseConfig";
import VitrineReparatur from "@/components/VitrineReparatur.vue";
import VitrineReparaturModal from "@/components/VitrineReparaturModal.vue";
import { useAppFlavorStore } from "@/stores/appFlavor";
import { usePwaInstallStore } from "@/stores/pwaInstallStore";

export default {
  name: "VitrineFooter",
  components: {
    VitrineReparatur,
    VitrineReparaturModal,
  },
  emits: ["open-legal"],
  data() {
    return {
      productId: "",
      isSearching: false,
      reparaturen: [],
      detailProduktId: "",
      detailProduktName: "",
      detailMarke: { name: "", modell: "" },
      showDetailModal: false,
      detailConsultedAt: null,
      notFoundId: "",
      notFoundMessage: "",
    };
  },
  computed: {
    appFlavor() {
      return useAppFlavorStore().flavor;
    },
    pwaInstall() {
      return usePwaInstallStore();
    },
    isValidId() {
      return Boolean(this.formatIdentifier(this.productId));
    },
  },
  methods: {
    logContact(type, detail) {
      if (!analytics) return;
      logEvent(analytics, "contact_click", {
        type: type,
        detail: detail || "footer",
      });
    },
    onInput() {
      this.limitInputLength();
      const formatted = this.formatIdentifier(this.productId);
      if (formatted && formatted.length === 9 && !this.isSearching) {
        this.trySearch(formatted);
      }
    },
    trySearch(forcedId) {
      if (this.isSearching) return;
      const formatted = forcedId || this.formatIdentifier(this.productId);
      if (!formatted) return;
      this.performSearch(formatted);
    },
    async performSearch(produktId) {
      this.isSearching = true;
      try {
        const db = getFirestore();
        const reparaturenRef = collection(db, "reparaturen");
        const q = query(reparaturenRef, where("produktID", "==", produktId));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          this.handleNotFound(produktId);
          return;
        }

        const consultedAt = new Date();
        const entries = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .sort(
            (a, b) => this.toMillis(b.erstelltAm) - this.toMillis(a.erstelltAm)
          );

        this.openDetailModal(produktId, entries, consultedAt);
      } catch (error) {
        console.error("[VitrineFooter] Falha ao consultar reparaturen:", error);
        this.handleNotFound(
          produktId,
          "Não foi possível consultar no momento. Tente novamente."
        );
      } finally {
        this.isSearching = false;
        this.productId = "";
      }
    },
    openDetailModal(produktId, entries, consultedAt) {
      const header = entries[0] || {};
      const markeRaw = header.marke;
      let marke = { name: "", modell: "" };

      if (markeRaw && typeof markeRaw === "object") {
        marke = {
          name: markeRaw.name || "",
          modell: markeRaw.modell || "",
        };
      } else if (typeof markeRaw === "string") {
        marke = { name: markeRaw, modell: "" };
      }

      this.detailProduktId = produktId;
      this.detailProduktName = header.name || "";
      this.detailMarke = marke;
      this.reparaturen = entries.map((entry) => ({
        status: entry.status || "",
        erstelltAm: entry.erstelltAm || null,
        zurueckgegebenAm: entry.zurueckgegebenAm || null,
        problemBeschreibung: entry.problemBeschreibung || "",
        loesung: entry.loesung || "",
      }));
      this.detailConsultedAt = consultedAt || new Date();
      this.showDetailModal = true;
    },
    handleNotFound(produktId, customMessage) {
      this.notFoundId = produktId;
      this.notFoundMessage =
        customMessage || "Schau dir die Produkt-ID an und versuch's nochmal.";
      nextTick(() => {
        const modal = this.$refs.notFoundModal;
        if (modal && typeof modal.show === "function") modal.show();
      });
    },
    onDetailClosed() {
      this.detailProduktId = "";
      this.detailProduktName = "";
      this.detailMarke = { name: "", modell: "" };
      this.reparaturen = [];
      this.detailConsultedAt = null;
    },
    onNotFoundClosed() {
      this.notFoundId = "";
      this.notFoundMessage = "";
    },
    limitInputLength() {
      const raw = String(this.productId || "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");

      if (/^[AKMSZBX]/.test(raw)) {
        const letter = raw[0];
        const digits = raw.slice(1).replace(/\D/g, "").slice(0, 8);
        this.productId = `${letter}${digits}`;
      } else {
        const digits = raw.replace(/\D/g, "").slice(0, 7);
        this.productId = digits;
      }
    },
    formatIdentifier(id) {
      const value = String(id || "")
        .toUpperCase()
        .trim();
      const novo = /^[AKMSZBX]\d{8}$/;
      const legado = /^\d{7}$/;

      if (novo.test(value)) return value;
      if (legado.test(value)) return `M0${value}`;
      return "";
    },
    toMillis(input) {
      const date = this.toDate(input);
      return date ? date.getTime() : 0;
    },
    toDate(input) {
      if (!input) return null;
      if (input instanceof Date) return input;
      if (input && typeof input.toDate === "function") {
        try {
          return input.toDate();
        } catch (_) {
          return null;
        }
      }
      if (typeof input === "number") {
        const parsed = new Date(input);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }
      if (typeof input === "string") {
        const normalized = input.trim();
        if (!normalized) return null;
        const parsed = new Date(normalized);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }
      return null;
    },
  },
};
</script>

<style scoped>
/* footer visual fica todo em classes utilitárias */
</style>
